import { useEffect, useMemo, useState } from "react";
import { Search, ArrowLeft, PhoneCall, PhoneIncoming, UserPlus, Sparkles, Voicemail, Play, RefreshCw, Inbox, Bot, Clock, Hand, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { channelMeta, intentMeta, timeAgo } from "@/lib/app-utils";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import CustomerProfilePanel from "@/components/CustomerProfilePanel";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "priority", label: "Priority" },
  { key: "mine", label: "Mine" },
  { key: "unread", label: "Unread" },
  { key: "active", label: "Active", needsContact: true },
];

const ESCALATION_LABEL = {
  human_request: "Human requested", negative_sentiment: "Negative sentiment",
  low_kb_confidence: "Bot couldn't answer", max_bot_turns: "Max bot turns",
  draft_review: "Draft ready", no_bot: "New", bot_error: "Bot error",
  bot_unavailable: "Bot offline (LLM)", send_failed: "Auto-send failed",
};

// Channel filter chips — second row beneath workflow filters.
const CHANNEL_CHIPS = [
  { key: "all" },
  { key: "voice" },
  { key: "whatsapp" },
  { key: "sms" },
  { key: "email" },
  { key: "webchat" },
  { key: "voicemail" },
  { key: "instagram" },
  { key: "facebook" },
  { key: "telegram" },
  { key: "viber" },
];

export default function OmniInbox({ selectedId, onSelect, view, onChangeView, contact, callDriven, onCall, activeSession, isOffline }) {
  const { flags: featureFlags } = useFeatureFlags();
  const [filter, setFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyVm, setBusyVm] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState([]);
  const [claiming, setClaiming] = useState(null);

  function fetchPending() {
    return api.get("/me/inbox/pending")
      .then((r) => setPending(r.data.pending || []))
      .catch(() => {});
  }

  async function claim(c) {
    setClaiming(c.id);
    try {
      const r = await api.post(`/me/conversations/${c.id}/claim`);
      setPending((prev) => prev.filter((x) => x.id !== c.id));
      await refresh(true);
      if (isOffline) {
        toast.success("Claimed — set yourself Available to open & reply");
      } else {
        toast.success("Claimed — it's yours now");
        onSelect(r.data.conversation || c);
      }
    } catch (e) {
      toast.error(formatErr(e));
      fetchPending();
    } finally { setClaiming(null); }
  }

  function refresh(silent = false) {
    if (!silent) setLoading(true);
    const p = filter === "active" ? "all" : filter;
    return api.get(`/conversations`, { params: { filter: p, q, unified: 1 } })
      .then((r) => setItems(r.data))
      .finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter, q]);

  // Messenger-like live inbox: refresh on every inbound WS event AND poll the
  // list silently every 10s so new messages / unread counts / previews update
  // without a manual reload.
  useEffect(() => {
    const onInbound = () => refresh(true);
    const onOrch = () => { refresh(true); fetchPending(); };
    window.addEventListener("channel:inbound", onInbound);
    window.addEventListener("orch:update", onOrch);
    fetchPending();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") { refresh(true); fetchPending(); }
    }, 10000);
    return () => { window.removeEventListener("channel:inbound", onInbound); window.removeEventListener("orch:update", onOrch); clearInterval(iv); };
    // eslint-disable-next-line
  }, [filter, q]);

  // Clear the unread badge instantly when a conversation is opened.
  function selectConversation(c) {
    if (c?.unread) setItems((prev) => prev.map((i) => i.id === c.id ? { ...i, unread: 0 } : i));
    onSelect(c);
  }

  async function syncInbox() {
    setSyncing(true);
    try {
      const r = await api.post(`/me/sync-inbox`);
      toast.success(`Synced ${r.data?.synced ?? 0} live thread(s)`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); } finally { setSyncing(false); }
  }

  const filteredItems = useMemo(() => {
    // Bot-owned + unclaimed pool conversations stay OUT of the agent's normal
    // list (they surface via the bot, or the "Pending" claim pool once
    // escalated). Admins/supervisors debug bot chats from the AI Orchestration
    // dashboard's Live Conversations panel. Legacy chats (no orch_state) show.
    const base = items.filter((c) => !["bot_handling", "pending_assignment"].includes(c.orch_state));
    if (channelFilter === "all") return base;
    return base.filter((c) => (c.channels && c.channels.length)
      ? c.channels.some((x) => x.channel === channelFilter)
      : c.primary_channel === channelFilter);
  }, [items, channelFilter]);
  // Only show channel chips the admin enabled (per role/group feature flags).
  const visibleChannelChips = useMemo(
    () => CHANNEL_CHIPS.filter((c) => c.key === "all" || featureFlags?.[`channel_${c.key}`] !== false),
    [featureFlags]
  );
  const total = filteredItems.length;
  const isActive = view === "active" && contact;

  function chooseFilter(k) {
    setFilter(k);
    if (k === "active" && contact) onChangeView("active");
    else onChangeView("list");
  }

  async function transcribeVm(c) {
    setBusyVm(c.id);
    try {
      const r = await api.post(`/conversations/${c.id}/transcribe-voicemail`);
      const engine = r.data?.engine || "simulator";
      const fb = r.data?.fallback_reason;
      const detail = fb ? ` (fallback: ${fb})` : "";
      toast.success(`Transcribed via ${engine}${detail}`);
      // Mutate this row optimistically
      setItems((prev) => prev.map((x) => x.id === c.id
        ? { ...x, voicemail_transcript_status: "ready", preview: r.data.transcript.slice(0, 160) }
        : x));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusyVm(null); }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-5">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight">Omni Inbox</h2>
          <div className="flex items-center gap-2">
            <button
              data-testid="inbox-sync-btn"
              onClick={syncInbox}
              disabled={syncing}
              title="Sync live channels (Instagram / Facebook / Telegram)"
              className="text-overline text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync
            </button>
            <span className="text-overline text-muted-foreground">{total} open</span>
          </div>
        </div>

        <div className="relative mt-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, number, topic…" className="pl-9 h-10 bg-muted/50 border-transparent focus-visible:bg-card" />
        </div>

        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {FILTERS.map((f) => {
            const active = (f.key === "active" ? isActive : filter === f.key && !isActive);
            // During an active call the agent is pinned to the customer lead
            // pop — lock every workflow filter except "Active".
            const callLocked = !!callDriven && f.key !== "active";
            const disabled = (f.needsContact && !contact) || callLocked;
            return (
              <button
                key={f.key}
                data-testid={`inbox-filter-${f.key}`}
                onClick={() => !disabled && chooseFilter(f.key)}
                disabled={disabled}
                title={callLocked ? "Locked during an active call" : undefined}
                className={`text-xs h-8 px-3 rounded-full border transition-colors ${active ? "bg-secondary text-secondary-foreground border-secondary" : "bg-transparent border-border text-muted-foreground hover:text-foreground"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {f.label}
                {f.key === "pending" && pending.length > 0 && (
                  <span className="ml-1.5 inline-grid place-items-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">{pending.length}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Channel filter chips (Slice 9) — hidden during an active call so
            the agent stays pinned to the customer lead pop. */}
        {!callDriven && (
        <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid="inbox-channel-row">
          {visibleChannelChips.map((c) => {
            const meta = c.key === "all" ? null : channelMeta[c.key];
            const Icon = meta?.Icon;
            const active = channelFilter === c.key;
            const count = c.key === "all" ? items.length : items.filter((i) => (i.channels && i.channels.length)
              ? i.channels.some((x) => x.channel === c.key)
              : i.primary_channel === c.key).length;
            const hidden = c.key !== "all" && count === 0;
            if (hidden) return null;
            return (
              <button
                key={c.key}
                data-testid={`inbox-channel-${c.key}`}
                onClick={() => setChannelFilter(c.key)}
                className={`text-[10px] uppercase tracking-wider h-6 px-2 rounded-full border inline-flex items-center gap-1 transition-colors ${active ? "bg-primary/10 text-primary border-primary/40" : "bg-transparent border-border/60 text-muted-foreground hover:text-foreground hover:border-border"}`}
                title={c.key === "all" ? "All channels" : meta?.label}
              >
                {Icon ? <Icon className="w-3 h-3"/> : null}
                <span>{c.key === "all" ? "All" : meta?.label}</span>
                <span className={`text-[9px] font-mono opacity-70`}>{count}</span>
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* Content */}
      {isActive ? (
        <div className="flex-1 min-h-0 flex flex-col" data-testid="inbox-active-section">
          <div className="px-5 pt-3 pb-2 flex items-center justify-between gap-2">
            {callDriven ? (
              <Badge variant="outline" data-testid="inbox-call-locked-pill" className="gap-1.5 text-[10px] uppercase tracking-wider border-border text-muted-foreground">
                <PhoneCall className="w-3 h-3" /> On call · customer lead
              </Badge>
            ) : (
              <Button variant="ghost" size="sm" data-testid="inbox-back-btn" onClick={() => { onChangeView("list"); setFilter("all"); }} className="gap-1.5 -ml-2">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to inbox
              </Button>
            )}
            {callDriven && activeSession && (
              <Badge
                data-testid="inbox-screen-pop-pill"
                variant="outline"
                className={`gap-1.5 text-[10px] uppercase tracking-wider ${activeSession.state === "ringing" ? "border-sky-400 text-sky-700 bg-sky-50 dark:bg-sky-950/30" : activeSession.state === "talking" ? "border-emerald-400 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30" : "border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/30"}`}
              >
                {activeSession.direction === "inbound" ? <PhoneIncoming className="w-3 h-3"/> : <PhoneCall className="w-3 h-3"/>}
                {activeSession.direction === "inbound" ? "Inbound call" : "Outbound call"} · {activeSession.state}
              </Badge>
            )}
          </div>
          <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
            <div className="px-3 pb-4">
              {contact?.unknown ? (
                <UnknownCallerCard contact={contact} />
              ) : (
                <CustomerProfilePanel contactId={contact.id} variant="inline" activeSession={activeSession} onCall={onCall} />
              )}
            </div>
          </ScrollArea>
        </div>
      ) : filter === "pending" ? (
        <ScrollArea className="flex-1 mt-3 [&_[data-radix-scroll-area-viewport]>div]:!block" data-testid="inbox-pending-pool">
          <ul className="px-3 pb-4 space-y-1.5">
            {pending.length === 0 && (
              <li className="text-center text-sm text-muted-foreground p-8" data-testid="inbox-pending-empty">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No conversations waiting to be claimed.
              </li>
            )}
            {pending.map((c) => {
              const ch = channelMeta[c.primary_channel] || channelMeta.system;
              const Icon = ch.Icon;
              return (
                <li key={c.id}>
                  <div className={`w-full text-left p-3 rounded-lg border ${c.recommended ? "border-primary/50 ring-1 ring-primary/30 bg-primary/5" : "border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/20"}`} data-testid={`inbox-pending-${c.id}`}>
                    <div className="flex gap-3">
                      <div className={`relative w-11 h-11 shrink-0 rounded-xl grid place-items-center ${ch.cls}`}>
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm truncate">{c.contact_name || "Unknown"}</div>
                          <span className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-bold shrink-0">{ESCALATION_LABEL[c.escalation_reason] || c.escalation_reason}</span>
                        </div>
                        <p className="text-xs truncate mt-0.5 text-muted-foreground">{c.preview}</p>
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                          {c.recommended && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary uppercase tracking-wider" data-testid={`inbox-best-match-${c.id}`}>
                              <Star className="w-3 h-3 fill-current" /> Best match
                            </span>
                          )}
                          {c.bot_draft && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 uppercase tracking-wider">
                              <Bot className="w-3 h-3" /> Draft ready
                            </span>
                          )}
                          {(c.required_skills || []).map((s) => (
                            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{s}</span>
                          ))}
                          <Button
                            data-testid={`inbox-claim-${c.id}`}
                            size="sm"
                            className="h-6 px-2.5 text-[10px] gap-1 bg-primary hover:bg-primary/90 text-primary-foreground ml-auto"
                            disabled={claiming === c.id}
                            onClick={() => claim(c)}
                          >
                            <Hand className="w-3 h-3" /> {claiming === c.id ? "Claiming…" : "Claim"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      ) : (
        <ScrollArea className="flex-1 mt-3 [&_[data-radix-scroll-area-viewport]>div]:!block">
          <ul className="px-3 pb-4 space-y-1.5">
            {loading && [...Array(4)].map((_, i) => <li key={i} className="h-20 rounded-lg bg-muted/40 animate-pulse" />)}
            {!loading && filteredItems.length === 0 && (
              <li className="text-center text-sm text-muted-foreground p-8" data-testid="inbox-empty">
                No conversations match this filter.
              </li>
            )}
            {!loading && filteredItems.map((c) => {
              const ch = channelMeta[c.primary_channel] || channelMeta.system;
              const intent = intentMeta[c.intent];
              const active = c.id === selectedId;
              const Icon = ch.Icon;
              const isVoicemail = c.primary_channel === "voicemail";
              const vmStatus = c.voicemail_transcript_status;
              return (
                <li key={c.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid={`inbox-item-${c.id}`}
                    onClick={() => selectConversation(c)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectConversation(c); } }}
                    className={`w-full text-left p-3 rounded-lg border transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${active ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-transparent hover:bg-muted/60"}`}
                  >
                    <div className="flex gap-3">
                      <div className={`relative w-11 h-11 shrink-0 rounded-xl grid place-items-center ${ch.cls}`}>
                        <Icon className="w-5 h-5" strokeWidth={2} />
                        {c.unread ? <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold ring-2 ring-card">{c.unread}</span> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm truncate flex items-center gap-2">
                            {c.contact_name}
                            {c.live_call && <span className="relative inline-block w-2 h-2 rounded-full bg-red-500 text-red-500 live-pulse" />}
                            {isVoicemail && (
                              <Badge variant="outline" className="h-4 text-[9px] uppercase gap-0.5">
                                <Voicemail className="w-2.5 h-2.5"/> {Math.floor((c.voicemail_duration||0)/60)}:{String((c.voicemail_duration||0)%60).padStart(2,"0")}
                              </Badge>
                            )}
                          </div>
                          <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(c.last_ts)}</span>
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${isVoicemail && vmStatus !== "ready" ? "italic text-muted-foreground/60" : "text-muted-foreground"}`}>
                          {c.preview}
                        </p>
                        {c.channels && c.channels.length > 1 && (
                          <div className="mt-1.5 flex items-center gap-1" data-testid={`inbox-item-channels-${c.id}`}>
                            {c.channels.map((x) => {
                              const cm = channelMeta[x.channel] || channelMeta.system;
                              const CI = cm.Icon;
                              const isRep = x.channel === c.primary_channel;
                              return (
                                <span
                                  key={x.channel}
                                  title={`${cm.label}${x.unread ? ` · ${x.unread} unread` : ""}`}
                                  className={`relative inline-grid place-items-center w-5 h-5 rounded-md ${isRep ? `${cm.cls} text-white` : "bg-muted text-muted-foreground"}`}
                                >
                                  <CI className="w-3 h-3" />
                                  {x.unread ? <span className="absolute -top-1 -right-1 min-w-[13px] h-[13px] px-0.5 grid place-items-center rounded-full bg-primary text-primary-foreground text-[8px] font-bold ring-1 ring-card">{x.unread}</span> : null}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                          {c.orch_state === "agent_assigned" && c.assigned_agent_name && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase tracking-wider">{c.assigned_agent_name}</span>
                          )}
                          {intent && (
                            <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ${intent.cls} uppercase tracking-wider`}>{intent.label}</span>
                          )}
                          {isVoicemail && vmStatus === "pending" && (
                            <Button
                              data-testid={`vm-transcribe-${c.id}`}
                              size="sm" variant="outline"
                              className="h-6 px-2 text-[10px] gap-1"
                              disabled={busyVm === c.id}
                              onClick={(e) => { e.stopPropagation(); transcribeVm(c); }}
                            >
                              <Sparkles className="w-3 h-3"/> {busyVm === c.id ? "Transcribing…" : "Transcribe"}
                            </Button>
                          )}
                          {isVoicemail && vmStatus === "ready" && (
                            <Badge className="h-5 text-[9px] bg-emerald-100 text-emerald-900 border-emerald-300">Transcribed</Badge>
                          )}
                          {isVoicemail && (
                            <Button
                              size="icon" variant="ghost" className="h-6 w-6"
                              data-testid={`vm-play-${c.id}`}
                              title="Play (simulator)"
                              onClick={(e) => { e.stopPropagation(); toast.message("Audio playback coming soon"); }}
                            >
                              <Play className="w-3 h-3"/>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}

function UnknownCallerCard({ contact }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 p-5 text-center" data-testid="inbox-unknown-caller">
      <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/60 mx-auto grid place-items-center">
        <UserPlus className="w-7 h-7 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="mt-3 font-display text-lg font-bold">{contact.full_name || "Unknown caller"}</div>
      <div className="text-sm font-mono text-muted-foreground">{contact.phone}</div>
      <div className="mt-3 text-xs text-muted-foreground">
        No CRM record matched this number. Save them as a contact to keep history.
      </div>
      <Button
        data-testid="inbox-save-unknown-btn"
        variant="outline"
        size="sm"
        className="mt-3 gap-1.5"
        onClick={async () => {
          try {
            const r = await api.post("/contacts", {
              full_name: contact.full_name || `Caller ${contact.phone}`,
              phone: contact.phone, email: "", company: "",
            });
            // Trigger a hard reload of the panel via URL change isn't easy here; toast + redirect
            window.dispatchEvent(new CustomEvent("contact:created", { detail: r.data }));
          } catch {}
        }}
      >
        <UserPlus className="w-3.5 h-3.5"/>Save as contact
      </Button>
    </div>
  );
}
